/**
 * TenantCaretakerCard — opiekun tenanta w szczegółach (H2 Etap B, krok 3).
 *
 * Podgląd (tylko-do-odczytu): kto jest opiekunem tenanta. Admin z uprawnieniem
 * tenants.caretakers.manage klika „Zarządzaj opiekunem” → wspólny modal
 * (TenantCaretakerManageModal) z przypisywaniem i usuwaniem. Ten sam modal jest
 * reużywany z listy tenantów.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { TenantCaretakerManageModal } from '@/components/tenant-detail/TenantCaretakerManageModal';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

interface Caretaker {
  id: string;
  adminUserId: string;
  adminName: string | null;
  adminEmail: string;
}
interface CaretakersResponse {
  caretakers: Caretaker[];
}

export function TenantCaretakerCard({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManageCaretakers = hasPermission('tenants.caretakers.manage');

  const [manageOpen, setManageOpen] = useState(false);

  const { data, isLoading } = useQuery<CaretakersResponse>({
    queryKey: ['admin', 'tenant', tenantId, 'caretakers'],
    queryFn: async () =>
      (await api.get<CaretakersResponse>(`/admin/tenants/${tenantId}/caretakers`)).data,
    enabled: !!tenantId,
  });

  const caretakers = useMemo(() => data?.caretakers ?? [], [data]);

  return (
    <Card padding="md">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">
          {t('admin.tenants.detail.caretaker.title')}
        </h3>
        {canManageCaretakers && (
          <Button variant="secondary" size="sm" onClick={() => setManageOpen(true)}>
            {t('admin.tenants.detail.caretaker.manageButton')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-text-muted">…</p>
      ) : caretakers.length === 0 ? (
        <p className="text-sm text-text-muted">{t('admin.tenants.detail.caretaker.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {caretakers.map((c) => (
            <li key={c.id} className="text-sm text-text-primary">
              {c.adminName || c.adminEmail}
              {c.adminName && (
                <span className="ml-2 text-xs text-text-tertiary">{c.adminEmail}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManageCaretakers && (
        <TenantCaretakerManageModal
          tenantId={manageOpen ? tenantId : null}
          open={manageOpen}
          onClose={() => setManageOpen(false)}
        />
      )}
    </Card>
  );
}
